/**
 * BuilderCompanyPreview — interactive preview of the builder-company feature.
 * Route: /builder-preview  (must be logged in — uses your real account so
 * companies you add here persist and show up in the real new-bid modal).
 *
 * Left: the real BuilderCompanyPicker (add/select companies + logo upload).
 * Right: a mock bid-request card showing how the invited contractor sees the
 * builder (name + logo) at the top of the bid.
 */
import React, { useState } from 'react';
import { Building2, FileText, MapPin, Send } from 'lucide-react';
import { BuilderCompanyPicker } from '@/components/BuilderCompanyPicker';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

export default function BuilderCompanyPreview() {
  let user = null;
  try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch { /* ignore */ }
  const userId = user?.id || user?.user_id || null;
  const [builder, setBuilder] = useState({ id: null, name: null, logo: null });

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="text-center text-[11px] font-semibold text-amber-900 bg-amber-300 py-1.5 px-4">
        PREVIEW · builder companies on bid requests · uses your real account
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-extrabold text-slate-900">Builder on bid requests</h1>
        <p className="text-sm text-slate-500 mt-1">
          Pick which of your companies a project is built under. Contractors see it at the top of the bid request.
        </p>

        {!userId && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            You need to be logged in to test this (it uses your account). Please log in, then reopen this link.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Left: picker */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-sm font-bold text-slate-800 mb-3">On the project setup</p>
            <BuilderCompanyPicker userId={userId} value={builder.id} onChange={setBuilder} />
            <p className="text-[11px] text-slate-400 mt-4">
              Try "Add company", upload a logo, and select it. It saves to your account and appears in the real
              "Start a new bid project" modal and Edit Project.
            </p>
          </div>

          {/* Right: mock bid request as the contractor sees it */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="bid-request-preview">
            <div className="px-5 py-3 text-white text-xs font-bold uppercase tracking-wide" style={{ background: NAVY }}>
              Bid request — contractor view
            </div>
            <div className="p-5">
              {/* Builder header */}
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                {builder.logo ? (
                  <img src={builder.logo} alt="" className="w-12 h-12 rounded-lg object-contain bg-white border border-slate-200" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">Bidding for</p>
                  <p className="text-base font-extrabold text-slate-900" data-testid="bid-request-builder-name">
                    {builder.name || 'Select a builder →'}
                  </p>
                </div>
              </div>
              {/* Sample project body */}
              <div className="mt-4 space-y-2">
                <p className="text-lg font-bold text-slate-900">Ellis Row Townhomes</p>
                <p className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> 1319-1333 Ellis Row, Atlanta GA</p>
                <div className="mt-3 rounded-lg bg-slate-50 p-3">
                  <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Scope: Electrical rough-in</p>
                  <p className="text-xs text-slate-500 mt-1">Plans attached · bids due in 7 days</p>
                </div>
              </div>
              <button className="mt-4 w-full text-white font-bold text-sm py-3 rounded-xl inline-flex items-center justify-center gap-2" style={{ background: GREEN }}>
                <Send className="w-4 h-4" /> Submit my bid
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
